import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';

const TRIPS_COLLECTION = 'trips';

/**
 * US States and Territories (2-letter codes and full names)
 */
const US_STATES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma',
  'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
  'Puerto Rico', 'Virgin Islands', 'Guam', 'American Samoa', 'Northern Mariana Islands'
]);

/**
 * Canadian Provinces and Territories
 */
const CANADIAN_PROVINCES = new Set([
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT',
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Nova Scotia', 'Northwest Territories', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon'
]);

/**
 * Known country names for validation
 */
const KNOWN_COUNTRIES = new Set([
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
  'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria',
  'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros',
  'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Czechia',
  'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'East Timor', 'Ecuador', 'Egypt', 'El Salvador',
  'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland',
  'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
  'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho',
  'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macedonia',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
  'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
  'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
  'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
  'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
  'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea',
  'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Swaziland', 'Sweden',
  'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe'
]);

/**
 * Activity Types - Predefined activity options
 */
export const ACTIVITY_TYPES = [
  // Tours & Sightseeing
  { value: 'bike-tour', label: 'Bike Tour', icon: 'fa-bicycle' },
  { value: 'boat-tour', label: 'Boat Tour', icon: 'fa-ship' },
  { value: 'city-tour', label: 'City Tour', icon: 'fa-bus' },
  { value: 'sightseeing', label: 'Sightseeing', icon: 'fa-camera' },
  { value: 'tour', label: 'Tour', icon: 'fa-bus' },
  { value: 'walking-tour', label: 'Walking Tour', icon: 'fa-walking' },

  // Water Activities
  { value: 'cruise', label: 'Cruise', icon: 'fa-ship' },
  { value: 'fishing', label: 'Fishing', icon: 'fa-fish' },
  { value: 'kayaking', label: 'Kayaking', icon: 'fa-water' },
  { value: 'paddleboarding', label: 'Paddleboarding', icon: 'fa-water' },
  { value: 'rafting', label: 'Rafting', icon: 'fa-water' },
  { value: 'sailing', label: 'Sailing', icon: 'fa-ship' },
  { value: 'scuba-diving', label: 'Scuba Diving', icon: 'fa-swimmer' },
  { value: 'snorkeling', label: 'Snorkeling', icon: 'fa-swimmer' },
  { value: 'surfing', label: 'Surfing', icon: 'fa-water' },
  { value: 'swimming', label: 'Swimming', icon: 'fa-swimmer' },

  // Outdoor & Adventure
  { value: 'atv-tour', label: 'ATV/Quad Biking', icon: 'fa-motorcycle' },
  { value: 'camping', label: 'Camping', icon: 'fa-campground' },
  { value: 'hiking', label: 'Hiking', icon: 'fa-hiking' },
  { value: 'horseback-riding', label: 'Horseback Riding', icon: 'fa-horse' },
  { value: 'hot-air-balloon', label: 'Hot Air Balloon', icon: 'fa-parachute-box' },
  { value: 'mountain-biking', label: 'Mountain Biking', icon: 'fa-biking' },
  { value: 'climbing', label: 'Rock Climbing', icon: 'fa-mountain' },
  { value: 'safari', label: 'Safari', icon: 'fa-binoculars' },
  { value: 'wildlife', label: 'Wildlife Watching', icon: 'fa-binoculars' },
  { value: 'zip-lining', label: 'Zip Lining', icon: 'fa-running' },

  // Winter Sports
  { value: 'ice-skating', label: 'Ice Skating', icon: 'fa-skating' },
  { value: 'skiing', label: 'Skiing', icon: 'fa-skiing' },
  { value: 'snowboarding', label: 'Snowboarding', icon: 'fa-snowboarding' },

  // Cultural & Historical
  { value: 'art-gallery', label: 'Art Gallery', icon: 'fa-palette' },
  { value: 'castle-visit', label: 'Castle Visit', icon: 'fa-fort-awesome' },
  { value: 'historical-site', label: 'Historical Site', icon: 'fa-landmark' },
  { value: 'monument', label: 'Monument Visit', icon: 'fa-monument' },
  { value: 'museum', label: 'Museum Visit', icon: 'fa-landmark' },
  { value: 'temple-visit', label: 'Temple/Religious Site', icon: 'fa-place-of-worship' },

  // Food & Drink
  { value: 'brewery-tour', label: 'Brewery Tour', icon: 'fa-beer' },
  { value: 'cooking-class', label: 'Cooking Class', icon: 'fa-utensils' },
  { value: 'food-tour', label: 'Food Tour', icon: 'fa-utensils' },
  { value: 'market-visit', label: 'Market Visit', icon: 'fa-shopping-basket' },
  { value: 'restaurant', label: 'Restaurant', icon: 'fa-utensils' },
  { value: 'wine-tasting', label: 'Wine Tasting', icon: 'fa-wine-glass-alt' },

  // Entertainment
  { value: 'concert', label: 'Concert', icon: 'fa-music' },
  { value: 'cultural-show', label: 'Cultural Show', icon: 'fa-theater-masks' },
  { value: 'festival', label: 'Festival', icon: 'fa-flag' },
  { value: 'live-music', label: 'Live Music', icon: 'fa-music' },
  { value: 'theater', label: 'Theater Show', icon: 'fa-theater-masks' },

  // Wellness & Relaxation
  { value: 'beach-day', label: 'Beach Day', icon: 'fa-umbrella-beach' },
  { value: 'massage', label: 'Massage', icon: 'fa-spa' },
  { value: 'spa', label: 'Spa', icon: 'fa-spa' },
  { value: 'yoga', label: 'Yoga', icon: 'fa-om' },

  // Shopping
  { value: 'craft-market', label: 'Craft Market', icon: 'fa-store' },
  { value: 'shopping', label: 'Shopping', icon: 'fa-shopping-bag' },
  { value: 'souvenir-shopping', label: 'Souvenir Shopping', icon: 'fa-gift' },

  // Sports & Recreation
  { value: 'golf', label: 'Golf', icon: 'fa-golf-ball' },
  { value: 'gym', label: 'Gym/Workout', icon: 'fa-dumbbell' },
  { value: 'running', label: 'Running', icon: 'fa-running' },
  { value: 'sports-event', label: 'Sports Event', icon: 'fa-football-ball' },

  // Nature & Photography
  { value: 'botanical-garden', label: 'Botanical Garden', icon: 'fa-leaf' },
  { value: 'national-park', label: 'National Park', icon: 'fa-tree' },
  { value: 'photography', label: 'Photography', icon: 'fa-camera' },
  { value: 'stargazing', label: 'Stargazing', icon: 'fa-moon' },
  { value: 'sunrise-viewing', label: 'Sunrise Viewing', icon: 'fa-sun' },
  { value: 'sunset-viewing', label: 'Sunset Viewing', icon: 'fa-sun' },
  { value: 'whale-watching', label: 'Whale Watching', icon: 'fa-water' },

  // Attractions
  { value: 'amusement-park', label: 'Amusement Park', icon: 'fa-gamepad' },
  { value: 'aquarium', label: 'Aquarium', icon: 'fa-fish' },
  { value: 'theme-park', label: 'Theme Park', icon: 'fa-ticket-alt' },
  { value: 'zoo', label: 'Zoo', icon: 'fa-hippo' },

  // Other
  { value: 'other', label: 'Other Activity', icon: 'fa-running' }
];

/**
 * Get activity icon based on type
 * @param {string} type - Activity type
 * @returns {string} FontAwesome icon class
 */
export const getActivityIcon = (type) => {
  const activity = ACTIVITY_TYPES.find(a => a.value === type);
  return activity ? activity.icon : 'fa-running';
};

/**
 * Convert location part to country name
 * Handles US states, Canadian provinces, and validates country names
 * Returns null if the location part is not a recognized country/state
 */
const normalizeToCountry = (locationPart) => {
  if (!locationPart) return null;

  const normalized = locationPart.trim();

  // Check if it's a US state
  if (US_STATES.has(normalized)) {
    return 'United States';
  }

  // Check if it's a Canadian province
  if (CANADIAN_PROVINCES.has(normalized)) {
    return 'Canada';
  }

  // Check for common country name variations
  const countryMapping = {
    'USA': 'United States',
    'US': 'United States',
    'U.S.': 'United States',
    'U.S.A.': 'United States',
    'UK': 'United Kingdom',
    'U.K.': 'United Kingdom'
  };

  if (countryMapping[normalized]) {
    return countryMapping[normalized];
  }

  // Only return the location if it's a known country
  if (KNOWN_COUNTRIES.has(normalized)) {
    return normalized;
  }

  // Not a recognized country, state, or province
  return null;
};

/**
 * Extract all unique countries from trip days and reservations
 * @param {Array} days - Array of trip days
 * @returns {Array} Array of unique country names
 */
export const extractCountriesFromDays = (days) => {
  if (!days || days.length === 0) return [];

  const countries = new Set();

  days.forEach(day => {
    // Extract from overnight location (format: "City, State/Country" or "City, Country")
    if (day.overnight) {
      const parts = day.overnight.split(',').map(p => p.trim());
      if (parts.length > 0) {
        // Try last part first (usually country or state)
        const lastPart = parts[parts.length - 1];
        const country = normalizeToCountry(lastPart);
        if (country) {
          countries.add(country);
        }
        // If 3+ parts, also check second-to-last (might be state when last is country)
        if (parts.length >= 3) {
          const secondLast = parts[parts.length - 2];
          const country2 = normalizeToCountry(secondLast);
          if (country2) {
            countries.add(country2);
          }
        }
      }
    }

    // Extract from hotel location
    if (day.hotel && day.hotel.location) {
      const parts = day.hotel.location.split(',').map(p => p.trim());
      if (parts.length > 0) {
        const lastPart = parts[parts.length - 1];
        const country = normalizeToCountry(lastPart);
        if (country) {
          countries.add(country);
        }
        if (parts.length >= 3) {
          const secondLast = parts[parts.length - 2];
          const country2 = normalizeToCountry(secondLast);
          if (country2) {
            countries.add(country2);
          }
        }
      }
    }

    // Extract from flights
    if (day.flights && day.flights.length > 0) {
      day.flights.forEach(flight => {
        // From departure location
        if (flight.from) {
          const fromParts = flight.from.split(',').map(p => p.trim());
          if (fromParts.length > 0) {
            const lastPart = fromParts[fromParts.length - 1];
            const country = normalizeToCountry(lastPart);
            if (country) {
              countries.add(country);
            }
            if (fromParts.length >= 3) {
              const secondLast = fromParts[fromParts.length - 2];
              const country2 = normalizeToCountry(secondLast);
              if (country2) {
                countries.add(country2);
              }
            }
          }
        }
        // From arrival location
        if (flight.to) {
          const toParts = flight.to.split(',').map(p => p.trim());
          if (toParts.length > 0) {
            const lastPart = toParts[toParts.length - 1];
            const country = normalizeToCountry(lastPart);
            if (country) {
              countries.add(country);
            }
            if (toParts.length >= 3) {
              const secondLast = toParts[toParts.length - 2];
              const country2 = normalizeToCountry(secondLast);
              if (country2) {
                countries.add(country2);
              }
            }
          }
        }
      });
    }

    // Extract from car rental location
    if (day.carRental && day.carRental.location) {
      const parts = day.carRental.location.split(',').map(p => p.trim());
      if (parts.length > 0) {
        const lastPart = parts[parts.length - 1];
        const country = normalizeToCountry(lastPart);
        if (country) {
          countries.add(country);
        }
        if (parts.length >= 3) {
          const secondLast = parts[parts.length - 2];
          const country2 = normalizeToCountry(secondLast);
          if (country2) {
            countries.add(country2);
          }
        }
      }
    }
  });

  return Array.from(countries).filter(c => c && c.length > 0);
};

/**
 * Update trip countries based on current days
 * @param {Object} tripRef - Firestore trip reference
 * @param {Array} days - Current trip days
 */
const updateTripCountries = async (tripRef, days) => {
  const countries = extractCountriesFromDays(days);
  await updateDoc(tripRef, {
    countries,
    updatedAt: serverTimestamp()
  });
};

// Create a new trip
export const createTrip = async (userId, tripData) => {
  try {
    console.log('createTrip called with userId:', userId);
    console.log('Trip data being saved:', { ...tripData, userId });

    const tripRef = await addDoc(collection(db, TRIPS_COLLECTION), {
      ...tripData,
      userId,
      sharedWith: [], // Initialize empty shared users array
      shareItinerary: tripData.shareItinerary || false, // Default to false
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('Trip created successfully with ID:', tripRef.id);

    // Fetch the created trip to get the actual data with server timestamps
    const createdTrip = await getDoc(tripRef);

    if (createdTrip.exists()) {
      const tripDataWithId = {
        id: createdTrip.id,
        ...createdTrip.data()
      };
      return { success: true, id: tripRef.id, data: tripDataWithId };
    } else {
      // Fallback if we can't read it back immediately
      return { success: true, id: tripRef.id };
    }
  } catch (error) {
    console.error('Error creating trip:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      userId,
      hasUserId: !!userId
    });

    // Return a more descriptive error message
    let errorMessage = error.message;
    if (error.code === 'permission-denied' || error.message.includes('permission')) {
      errorMessage = 'Insufficient permissions to create trip. Please ensure you are logged in with a verified email address.';
    }

    return { success: false, error: errorMessage };
  }
};

// Get all trips for a user
export const getUserTrips = async (userId) => {
  try {
    console.log('Fetching trips for user:', userId);
    
    const q = query(
      collection(db, TRIPS_COLLECTION),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    const trips = [];

    querySnapshot.forEach((doc) => {
      trips.push({
        id: doc.id,
        ...doc.data()
      });
    });

    console.log('Fetched trips:', trips.length);

    // Sort by createdAt client-side to avoid index requirement
    trips.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });

    return { success: true, data: trips };
  } catch (error) {
    console.error('Error fetching trips:', error);
    console.error('Error details:', error.message, error.code);
    return { success: false, error: error.message };
  }
};

// Get a single trip by ID
export const getTrip = async (tripId, userId) => {
  try {
    const tripDoc = await getDoc(doc(db, TRIPS_COLLECTION, tripId));

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check if user owns this trip, it's shared with them, or it's a public itinerary
    if (tripData.userId !== userId && !tripData.sharedWith?.includes(userId) && !tripData.shareItinerary) {
      return { success: false, error: 'Not authorized to view this trip' };
    }

    return {
      success: true,
      data: {
        id: tripDoc.id,
        ...tripData
      }
    };
  } catch (error) {
    console.error('Error fetching trip:', error);
    return { success: false, error: error.message };
  }
};

// Update a trip
export const updateTrip = async (tripId, userId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    // Check ownership
    if (tripDoc.data().userId !== userId) {
      return { success: false, error: 'Not authorized to update this trip' };
    }

    await updateDoc(tripRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating trip:', error);
    return { success: false, error: error.message };
  }
};

// Delete a trip
export const deleteTrip = async (tripId, userId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    // Check ownership
    if (tripDoc.data().userId !== userId) {
      return { success: false, error: 'Not authorized to delete this trip' };
    }

    await deleteDoc(tripRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting trip:', error);
    return { success: false, error: error.message };
  }
};

// Add a hotel to a trip
export const addHotelToTrip = async (userId, tripId, hotelData) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const hotels = tripData.hotels || [];
    hotels.push({
      ...hotelData,
      id: Date.now().toString(),
      addedAt: new Date().toISOString()
    });

    await updateDoc(tripRef, {
      hotels,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding hotel:', error);
    return { success: false, error: error.message };
  }
};

// Add a day to a trip
export const addDayToTrip = async (tripId, userId, dayData) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    days.push({
      ...dayData,
      id: Date.now().toString() // Simple ID for the day
    });

    // Update countries based on all days
    const countries = extractCountriesFromDays(days);

    // Recalculate stays
    const existingStays = tripData.stays || [];
    const stays = computeStaysFromDays(days, existingStays);

    await updateDoc(tripRef, {
      days,
      countries,
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error adding day:', error);
    return { success: false, error: error.message };
  }
};

// Update a specific day in a trip
export const updateDayInTrip = async (tripId, userId, dayId, dayUpdates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    // Merge updates with existing day data
    const updatedDay = {
      ...days[dayIndex],
      ...dayUpdates
    };

    // Clean up null values by removing them entirely from the object
    // This ensures fields like hotel, carRental, etc. are properly deleted
    Object.keys(updatedDay).forEach(key => {
      if (updatedDay[key] === null) {
        delete updatedDay[key];
      }
    });

    days[dayIndex] = updatedDay;

    // Update countries based on all days (in case location changed)
    const countries = extractCountriesFromDays(days);

    // Recalculate stays
    const existingStays = tripData.stays || [];
    const stays = computeStaysFromDays(days, existingStays);

    await updateDoc(tripRef, {
      days,
      countries,
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating day:', error);
    return { success: false, error: error.message };
  }
};

// Remove a day from a trip
export const removeDayFromTrip = async (tripId, userId, dayId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const filteredDays = days.filter(d => d.id !== dayId);

    // Recalculate countries after removing the day
    const countries = extractCountriesFromDays(filteredDays);

    // Recalculate stays
    const existingStays = tripData.stays || [];
    const stays = computeStaysFromDays(filteredDays, existingStays);

    await updateDoc(tripRef, {
      days: filteredDays,
      countries,
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing day:', error);
    return { success: false, error: error.message };
  }
};

// Share trip with another user
export const shareTrip = async (tripId, userId, shareWithUserId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to share this trip' };
    }

    const sharedWith = tripData.sharedWith || [];
    if (!sharedWith.includes(shareWithUserId)) {
      sharedWith.push(shareWithUserId);
    }

    await updateDoc(tripRef, {
      sharedWith,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error sharing trip:', error);
    return { success: false, error: error.message };
  }
};

// ============= TODO MANAGEMENT =============

// Malaria risk countries (simplified list)
const MALARIA_RISK_COUNTRIES = new Set([
  'Afghanistan', 'Angola', 'Bangladesh', 'Benin', 'Bolivia', 'Botswana',
  'Brazil', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon',
  'Central African Republic', 'Chad', 'Colombia', 'Comoros', 'Congo',
  'Democratic Republic of the Congo', 'Ecuador', 'Equatorial Guinea',
  'Eritrea', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'India', 'Indonesia',
  'Ivory Coast', 'Kenya', 'Laos', 'Liberia', 'Madagascar', 'Malawi',
  'Mali', 'Mauritania', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal',
  'Nicaragua', 'Niger', 'Nigeria', 'Pakistan', 'Panama', 'Papua New Guinea',
  'Paraguay', 'Peru', 'Philippines', 'Rwanda', 'Sao Tome and Principe',
  'Senegal', 'Sierra Leone', 'Solomon Islands', 'Somalia', 'South Africa',
  'South Sudan', 'Sri Lanka', 'Sudan', 'Suriname', 'Tanzania', 'Thailand',
  'Togo', 'Uganda', 'Vanuatu', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia',
  'Zimbabwe'
]);

/**
 * Add a todo to a trip (trip-level todo)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} text - Todo text
 * @returns {Object} Result with success status
 */
export const addTodoToTrip = async (tripId, userId, text) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const todos = tripData.todos || [];
    const newTodo = {
      id: Date.now().toString(),
      text,
      completed: false,
      createdAt: new Date().toISOString()
    };

    todos.push(newTodo);

    await updateDoc(tripRef, {
      todos,
      updatedAt: serverTimestamp()
    });

    return { success: true, todo: newTodo };
  } catch (error) {
    console.error('Error adding todo to trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a todo in a trip (trip-level todo)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} todoId - Todo ID
 * @param {Object} updates - Updates to apply (text, completed)
 * @returns {Object} Result with success status
 */
export const updateTodoInTrip = async (tripId, userId, todoId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const todos = tripData.todos || [];
    const todoIndex = todos.findIndex(t => t.id === todoId);

    if (todoIndex === -1) {
      return { success: false, error: 'Todo not found' };
    }

    todos[todoIndex] = {
      ...todos[todoIndex],
      ...updates
    };

    await updateDoc(tripRef, {
      todos,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating todo in trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a todo from a trip (trip-level todo)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} todoId - Todo ID
 * @returns {Object} Result with success status
 */
export const removeTodoFromTrip = async (tripId, userId, todoId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const todos = tripData.todos || [];
    const filteredTodos = todos.filter(t => t.id !== todoId);

    await updateDoc(tripRef, {
      todos: filteredTodos,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing todo from trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a todo to a specific day
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} dayId - Day ID
 * @param {string} text - Todo text
 * @returns {Object} Result with success status
 */
export const addTodoToDay = async (tripId, userId, dayId, text) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    const dayTodos = days[dayIndex].todos || [];
    const newTodo = {
      id: Date.now().toString(),
      text,
      completed: false,
      createdAt: new Date().toISOString()
    };

    dayTodos.push(newTodo);
    days[dayIndex].todos = dayTodos;

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true, todo: newTodo };
  } catch (error) {
    console.error('Error adding todo to day:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a todo in a specific day
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} dayId - Day ID
 * @param {string} todoId - Todo ID
 * @param {Object} updates - Updates to apply (text, completed)
 * @returns {Object} Result with success status
 */
export const updateTodoInDay = async (tripId, userId, dayId, todoId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    const dayTodos = days[dayIndex].todos || [];
    const todoIndex = dayTodos.findIndex(t => t.id === todoId);

    if (todoIndex === -1) {
      return { success: false, error: 'Todo not found' };
    }

    dayTodos[todoIndex] = {
      ...dayTodos[todoIndex],
      ...updates
    };

    days[dayIndex].todos = dayTodos;

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating todo in day:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a todo from a specific day
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} dayId - Day ID
 * @param {string} todoId - Todo ID
 * @returns {Object} Result with success status
 */
export const removeTodoFromDay = async (tripId, userId, dayId, todoId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    const dayTodos = days[dayIndex].todos || [];
    const filteredTodos = dayTodos.filter(t => t.id !== todoId);

    days[dayIndex].todos = filteredTodos;

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing todo from day:', error);
    return { success: false, error: error.message };
  }
};

// ============= INSIGHT TODO MANAGEMENT =============

/**
 * Dismiss an insight todo (mark it so it won't be suggested again)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} insightType - Type of insight (e.g., 'missing-hotel', 'malaria-pills')
 * @param {string} dayId - Day ID (optional, for day-level insights)
 * @returns {Object} Result with success status
 */
export const dismissInsight = async (tripId, userId, insightType, dayId = null) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const dismissedInsights = tripData.dismissedInsights || [];
    const dismissal = dayId
      ? { insightType, dayId }
      : { insightType };

    // Check if already dismissed
    const alreadyDismissed = dismissedInsights.some(d =>
      d.insightType === insightType &&
      (dayId ? d.dayId === dayId : !d.dayId)
    );

    if (!alreadyDismissed) {
      dismissedInsights.push(dismissal);

      await updateDoc(tripRef, {
        dismissedInsights,
        updatedAt: serverTimestamp()
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error dismissing insight:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Undismiss an insight (allow it to appear again)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID
 * @param {string} insightType - Type of insight (e.g., 'missing-hotel', 'malaria-pills')
 * @param {string} dayId - Day ID (optional, for day-level insights)
 * @returns {Object} Result with success status
 */
export const undismissInsight = async (tripId, userId, insightType, dayId = null) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const dismissedInsights = tripData.dismissedInsights || [];

    // Remove the matching dismissed insight
    const updatedDismissedInsights = dismissedInsights.filter(d =>
      !(d.insightType === insightType && (dayId ? d.dayId === dayId : !d.dayId))
    );

    // Only update if something was removed
    if (updatedDismissedInsights.length !== dismissedInsights.length) {
      await updateDoc(tripRef, {
        dismissedInsights: updatedDismissedInsights,
        updatedAt: serverTimestamp()
      });
    }

    return { success: true };
  } catch (error) {
    console.error('Error undismissing insight:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Generate insight todos for a trip
 * @param {Object} trip - Trip data
 * @param {boolean} showInsights - User preference for showing insights
 * @returns {Object} Object containing tripInsights and dayInsights arrays
 */
export const generateInsightTodos = (trip, showInsights = true) => {
  if (!showInsights) {
    return { tripInsights: [], dayInsights: {} };
  }

  const tripInsights = [];
  const dayInsights = {};
  const dismissedInsights = trip.dismissedInsights || [];

  // Helper to check if insight is dismissed
  const isDismissed = (insightType, dayId = null) => {
    return dismissedInsights.some(d =>
      d.insightType === insightType &&
      (dayId ? d.dayId === dayId : !d.dayId)
    );
  };

  // Trip-level insight: Malaria risk
  const hasMalariaRisk = trip.countries?.some(country =>
    MALARIA_RISK_COUNTRIES.has(country)
  );

  if (hasMalariaRisk && !isDismissed('malaria-pills')) {
    const malariaCountries = trip.countries.filter(c => MALARIA_RISK_COUNTRIES.has(c));
    tripInsights.push({
      id: 'insight-malaria-pills',
      text: `Consider anti-malaria medication for ${malariaCountries.join(', ')}`,
      completed: false,
      isInsight: true,
      insightType: 'malaria-pills',
      createdAt: new Date().toISOString()
    });
  }

  // Helper to check if there's an overnight flight
  const hasOvernightFlight = (day) => {
    if (!day.flights || day.flights.length === 0) return false;

    return day.flights.some(flight => {
      // Flight arrives the next day or later
      if (flight.arrivalDayOffset && flight.arrivalDayOffset >= 1) {
        return true;
      }

      // Check for red-eye flight (departs late, arrives early)
      if (flight.departureTime && flight.arrivalTime) {
        const depHour = parseInt(flight.departureTime.split(':')[0]);
        const arrHour = parseInt(flight.arrivalTime.split(':')[0]);

        // Departs after 8 PM (20:00) and arrives before 6 AM (06:00)
        // assuming same day arrival (no offset)
        if (!flight.arrivalDayOffset || flight.arrivalDayOffset === 0) {
          return false; // Same day arrival, not overnight
        }

        // With day offset of 1, any late departure is likely overnight
        if (flight.arrivalDayOffset === 1 && depHour >= 20) {
          return true;
        }
      }

      return false;
    });
  };

  // Day-level insights: Missing hotel booking
  if (trip.days) {
    // Sort days by date to identify the last day
    const sortedDays = [...trip.days]
      .filter(day => day.date) // Only include days with dates
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Get the last day's ID (usually the return home day)
    const lastDayId = sortedDays.length > 0 ? sortedDays[sortedDays.length - 1].id : null;

    trip.days.forEach(day => {
      const dayId = day.id;

      // Check if day has a valid hotel booking
      // Hotel must exist as an object AND have a name (not just an empty object)
      const hasHotel = day.hotel && typeof day.hotel === 'object' &&
                       Object.keys(day.hotel).length > 0 &&
                       day.hotel.name &&
                       day.hotel.name.trim() !== '';

      const isOvernightFlight = hasOvernightFlight(day);
      const isLastDay = dayId === lastDayId;

      // Only suggest hotel if:
      // - No hotel booking AND
      // - No overnight flight AND
      // - Not the last day (typically return home day) AND
      // - Not dismissed
      if (!hasHotel && !isOvernightFlight && !isLastDay && !isDismissed('missing-hotel', dayId)) {
        if (!dayInsights[dayId]) {
          dayInsights[dayId] = [];
        }
        dayInsights[dayId].push({
          id: `insight-missing-hotel-${dayId}`,
          text: 'Add hotel booking details',
          completed: false,
          isInsight: true,
          insightType: 'missing-hotel',
          createdAt: new Date().toISOString()
        });
      }
    });
  }

  return { tripInsights, dayInsights };
};

// ============= TRIP SHARING FUNCTIONS =============

/**
 * Send a trip invitation via email
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID (trip owner)
 * @param {string} recipientEmail - Email address of the recipient
 * @param {string} accessLevel - Access level ('view-only' or 'edit')
 * @returns {Object} Result with success status and invite code
 */
export const inviteTripByEmail = async (tripId, userId, recipientEmail, accessLevel = 'view-only') => {
  try {
    const sendTripInvite = httpsCallable(functions, 'sendTripInvite');
    const result = await sendTripInvite({
      tripId,
      recipientEmail,
      accessLevel
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error inviting user to trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Invite user to trip by username (in-app notification)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID (person sending the invite)
 * @param {string} recipientUsername - Username of the recipient
 * @param {string} accessLevel - 'view-only' or 'edit'
 * @returns {Object} Result with success status and data
 */
export const inviteTripByUsername = async (tripId, userId, recipientUsername, accessLevel = 'view-only') => {
  try {
    const sendTripInviteByUsername = httpsCallable(functions, 'sendTripInviteByUsername');
    const result = await sendTripInviteByUsername({
      tripId,
      recipientUsername,
      accessLevel
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error inviting user to trip by username:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Accept a trip invitation
 * @param {string} inviteCode - Unique invitation code
 * @param {string} userId - User ID (person accepting the invite)
 * @returns {Object} Result with success status and trip details
 */
export const acceptTripInvite = async (inviteCode, userId) => {
  try {
    const acceptInvite = httpsCallable(functions, 'acceptTripInvite');
    const result = await acceptInvite({ inviteCode });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error accepting trip invite:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reject a trip invitation
 * @param {string} inviteCode - Unique invitation code
 * @param {string} userId - User ID (person rejecting the invite)
 * @returns {Object} Result with success status
 */
export const rejectTripInvite = async (inviteCode, userId) => {
  try {
    const rejectInvite = httpsCallable(functions, 'rejectTripInvite');
    const result = await rejectInvite({ inviteCode });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error rejecting trip invite:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Revoke trip sharing for a user (remove their access)
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID (trip owner)
 * @param {string} sharedUserId - User ID to revoke access from
 * @returns {Object} Result with success status
 */
export const revokeTripShare = async (tripId, userId, sharedUserId) => {
  try {
    const revokeShare = httpsCallable(functions, 'revokeTripShare');
    const result = await revokeShare({
      tripId,
      sharedUserId
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error revoking trip share:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Revoke pending trip invitation
 * @param {string} tripId - Trip ID
 * @param {string} userId - User ID (trip owner)
 * @param {string} inviteEmail - Email address of pending invite to revoke
 * @returns {Object} Result with success status
 */
export const revokePendingInvite = async (tripId, userId, inviteEmail) => {
  try {
    const revokeInvite = httpsCallable(functions, 'revokePendingInvite');
    const result = await revokeInvite({
      tripId,
      inviteEmail
    });

    return { success: true, data: result.data };
  } catch (error) {
    console.error('Error revoking pending invite:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all trips shared with the current user
 * @param {string} userId - User ID
 * @returns {Object} Result with trips array
 */
export const getSharedWithMeTrips = async (userId) => {
  try {
    const tripsRef = collection(db, TRIPS_COLLECTION);
    const q = query(
      tripsRef,
      where('sharedWith', 'array-contains', userId)
    );

    const querySnapshot = await getDocs(q);
    const trips = [];

    querySnapshot.forEach((doc) => {
      trips.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, trips };
  } catch (error) {
    console.error('Error getting shared trips:', error);
    return { success: false, error: error.message, trips: [] };
  }
};

/**
 * Get shared users for a trip
 * @param {string} tripId - Trip ID
 * @returns {Object} Result with sharedUsers array
 */
export const getSharedUsers = async (tripId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found', sharedUsers: [] };
    }

    const tripData = tripDoc.data();
    const sharedUsers = tripData.sharedUsers || [];
    const pendingInvites = tripData.pendingInvites || {};

    // Convert pendingInvites object to array
    // Note: The key is an escaped email (dots replaced with commas for Firestore)
    // The actual email is stored in invite.email
    const pending = Object.entries(pendingInvites)
      .filter(([escapedEmail, invite]) => invite.status === 'pending')
      .map(([escapedEmail, invite]) => ({
        email: invite.email || escapedEmail.replace(/,/g, '.'), // Use stored email or unescape
        accessLevel: invite.accessLevel,
        status: 'pending',
        sentAt: invite.sentAt
      }));

    return {
      success: true,
      sharedUsers,
      pendingInvites: pending
    };
  } catch (error) {
    console.error('Error getting shared users:', error);
    return { success: false, error: error.message, sharedUsers: [], pendingInvites: [] };
  }
};

/**
 * Check if a user has edit access to a trip
 * @param {Object} trip - Trip data
 * @param {string} userId - User ID to check
 * @returns {boolean} True if user can edit the trip
 */
export const canEditTrip = (trip, userId) => {
  // Owner can always edit
  if (trip.userId === userId) {
    return true;
  }

  // Check if user has edit access in sharedUsers
  const sharedUser = trip.sharedUsers?.find(su => su.userId === userId);
  return sharedUser?.accessLevel === 'edit';
};

/**
 * Check if a user has view access to a trip
 * @param {Object} trip - Trip data
 * @param {string} userId - User ID to check
 * @returns {boolean} True if user can view the trip
 */
export const canViewTrip = (trip, userId) => {
  // Owner can always view
  if (trip.userId === userId) {
    return true;
  }

  // Check if user is in sharedWith array
  return trip.sharedWith?.includes(userId) || false;
};

// ============= ITINERARY FUNCTIONS =============

/**
 * Extract itinerary data from a trip
 * Includes only: day numbers, location, flight from/to, notes, hotel names
 * Excludes: dates, booking codes, names, todos, insights
 * @param {Object} trip - Full trip data
 * @returns {Object} Itinerary data
 */
export const extractItinerary = (trip) => {
  if (!trip) return null;

  const itinerary = {
    id: trip.id,
    name: trip.name,
    description: trip.description,
    coverImage: trip.coverImage,
    countries: trip.countries,
    createdAt: trip.createdAt,
    tripImages: trip.tripImages || [],
    days: []
  };

  if (trip.days && trip.days.length > 0) {
    itinerary.days = trip.days.map((day, index) => {
      const itineraryDay = {
        dayNumber: index + 1,
        location: day.overnight || '',
        // Notes removed - may contain confirmation numbers or personal info
        hotel: day.hotel?.name ? {
          name: day.hotel.name,
          location: day.hotel.location || '',
          vendor: day.hotel.vendor || '' // For showing icons (Airbnb, Booking.com, etc.)
        } : null,
        carRental: day.carRental ? {
          vendor: day.carRental.vendor || '',
          location: day.carRental.location || ''
        } : null,
        flights: []
      };

      // Extract flight from/to (no booking codes or names)
      if (day.flights && day.flights.length > 0) {
        itineraryDay.flights = day.flights.map(flight => ({
          from: flight.from || '',
          to: flight.to || '',
          departureTime: flight.departureTime || '',
          arrivalTime: flight.arrivalTime || '',
          arrivalDayOffset: flight.arrivalDayOffset || 0,
          airline: flight.airline || '',
          departureCity: flight.departureCity || '',
          arrivalCity: flight.arrivalCity || '',
          departureAirport: flight.departureAirport || '',
          arrivalAirport: flight.arrivalAirport || ''
        }));
      }

      return itineraryDay;
    });
  }

  return itinerary;
};

/**
 * Get all shared itineraries (trips where shareItinerary is true)
 * @returns {Object} Result with itineraries array
 */
export const getSharedItineraries = async () => {
  try {
    console.log('Fetching shared itineraries...');
    const tripsRef = collection(db, TRIPS_COLLECTION);
    const q = query(
      tripsRef,
      where('shareItinerary', '==', true)
    );

    const querySnapshot = await getDocs(q);
    console.log('Found shared trips:', querySnapshot.size);
    const itineraries = [];

    querySnapshot.forEach((doc) => {
      const tripData = {
        id: doc.id,
        ...doc.data()
      };
      console.log('Trip shareItinerary value:', doc.id, tripData.shareItinerary);
      // Extract only itinerary data
      const itinerary = extractItinerary(tripData);
      if (itinerary) {
        itineraries.push(itinerary);
      }
    });

    // Sort by createdAt client-side
    itineraries.sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });

    console.log('Returning', itineraries.length, 'shared itineraries');
    return { success: true, itineraries };
  } catch (error) {
    console.error('Error fetching shared itineraries:', error);
    return { success: false, error: error.message, itineraries: [] };
  }
};

// ============================================================================
// STAY MANAGEMENT
// ============================================================================

/**
 * Compute stays from days by grouping consecutive days with same overnight location
 * Preserves existing stay data (todos, notes, details) when stays match
 * @param {Array} days - Array of day objects
 * @param {Array} existingStays - Array of existing stay objects (optional)
 * @returns {Array} Array of stay objects
 */
export const computeStaysFromDays = (days, existingStays = []) => {
  if (!days || days.length === 0) {
    return [];
  }

  // Filter days that have an overnight location and sort by date
  const daysWithOvernight = days
    .filter(day => day.overnight && day.overnight.trim() !== '')
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

  if (daysWithOvernight.length === 0) {
    return [];
  }

  const stays = [];
  let currentStay = null;

  daysWithOvernight.forEach((day, index) => {
    const dayDate = new Date(day.date);

    if (!currentStay) {
      // Start a new stay
      currentStay = {
        overnight: day.overnight,
        startDate: day.date,
        endDate: day.date,
        dayIds: [day.id]
      };
    } else {
      const prevDay = daysWithOvernight[index - 1];
      const prevDate = new Date(prevDay.date);
      const isConsecutive = Math.abs(dayDate - prevDate) <= 86400000; // 1 day in milliseconds
      const isSameLocation = day.overnight === currentStay.overnight;

      if (isConsecutive && isSameLocation) {
        // Extend current stay
        currentStay.endDate = day.date;
        currentStay.dayIds.push(day.id);
      } else {
        // Finish current stay and start a new one
        stays.push(currentStay);
        currentStay = {
          overnight: day.overnight,
          startDate: day.date,
          endDate: day.date,
          dayIds: [day.id]
        };
      }
    }
  });

  // Don't forget to push the last stay
  if (currentStay) {
    stays.push(currentStay);
  }

  // Filter to only include stays with 2+ days
  const multiDayStays = stays.filter(stay => stay.dayIds.length >= 2);

  // Now match with existing stays to preserve todos/notes/details
  const computedStays = multiDayStays.map(stay => {
    // Try to find matching existing stay
    // Match by: same overnight location AND overlapping date range
    const existingStay = existingStays.find(existing => {
      if (existing.overnight !== stay.overnight) return false;

      // Check if date ranges overlap or match
      const existingStart = new Date(existing.startDate);
      const existingEnd = new Date(existing.endDate);
      const newStart = new Date(stay.startDate);
      const newEnd = new Date(stay.endDate);

      // For the logic: if a stay's overnight changes and it splits,
      // we keep the data with the earlier stay. So we match if the
      // existing start date is <= new start date (i.e., the new stay
      // starts within or after the existing stay)
      return existingStart <= newStart && existingEnd >= existingStart;
    });

    return {
      id: existingStay?.id || `stay-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      overnight: stay.overnight,
      startDate: stay.startDate,
      endDate: stay.endDate,
      dayIds: stay.dayIds,
      todos: existingStay?.todos || [],
      activities: existingStay?.activities || [],
      notes: existingStay?.notes || '',
      details: existingStay?.details || ''
    };
  });

  return computedStays;
};

/**
 * Recalculate and update stays for a trip
 * Called after days are added/updated/deleted
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @returns {Object} Result object
 */
export const recalculateStays = async (tripId, userId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const existingStays = tripData.stays || [];

    // Compute new stays
    const stays = computeStaysFromDays(days, existingStays);

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true, stays };
  } catch (error) {
    console.error('Error recalculating stays:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update stay data (notes, details)
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {Object} stayUpdates - Updates to apply (can include notes, details)
 * @returns {Object} Result object
 */
export const updateStayInTrip = async (tripId, userId, stayId, stayUpdates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    // Update the stay
    stays[stayIndex] = {
      ...stays[stayIndex],
      ...stayUpdates
    };

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating stay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a todo to a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {string} todoText - The todo text
 * @returns {Object} Result object
 */
export const addTodoToStay = async (tripId, userId, stayId, todoText) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    const newTodo = {
      id: Date.now().toString(),
      text: todoText,
      completed: false,
      createdAt: new Date().toISOString()
    };

    stays[stayIndex].todos = stays[stayIndex].todos || [];
    stays[stayIndex].todos.push(newTodo);

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true, todo: newTodo };
  } catch (error) {
    console.error('Error adding todo to stay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a todo in a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {string} todoId - The todo ID
 * @param {Object} updates - Updates to apply (text, completed)
 * @returns {Object} Result object
 */
export const updateTodoInStay = async (tripId, userId, stayId, todoId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    const todos = stays[stayIndex].todos || [];
    const todoIndex = todos.findIndex(t => t.id === todoId);

    if (todoIndex === -1) {
      return { success: false, error: 'Todo not found' };
    }

    todos[todoIndex] = {
      ...todos[todoIndex],
      ...updates
    };

    stays[stayIndex].todos = todos;

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating todo in stay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a todo from a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {string} todoId - The todo ID
 * @returns {Object} Result object
 */
export const removeTodoFromStay = async (tripId, userId, stayId, todoId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    stays[stayIndex].todos = (stays[stayIndex].todos || []).filter(t => t.id !== todoId);

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing todo from stay:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================================
// ACTIVITY MANAGEMENT
// ============================================================================

/**
 * Add an activity to a day
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} dayId - The day ID
 * @param {Object} activityData - Activity data (type, vendor, cost, description, memberIds)
 * @returns {Object} Result object
 */
export const addActivityToDay = async (tripId, userId, dayId, activityData) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    const newActivity = {
      id: Date.now().toString(),
      type: activityData.type || '',
      vendor: activityData.vendor || '',
      cost: activityData.cost || '',
      description: activityData.description || '',
      memberIds: activityData.memberIds || [], // Empty array means all members
      createdAt: new Date().toISOString()
    };

    days[dayIndex].activities = days[dayIndex].activities || [];
    days[dayIndex].activities.push(newActivity);

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true, activity: newActivity };
  } catch (error) {
    console.error('Error adding activity to day:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an activity in a day
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} dayId - The day ID
 * @param {string} activityId - The activity ID
 * @param {Object} updates - Updates to apply (type, vendor, cost, description)
 * @returns {Object} Result object
 */
export const updateActivityInDay = async (tripId, userId, dayId, activityId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    const activities = days[dayIndex].activities || [];
    const activityIndex = activities.findIndex(a => a.id === activityId);

    if (activityIndex === -1) {
      return { success: false, error: 'Activity not found' };
    }

    activities[activityIndex] = {
      ...activities[activityIndex],
      ...updates
    };

    days[dayIndex].activities = activities;

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating activity in day:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove an activity from a day
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} dayId - The day ID
 * @param {string} activityId - The activity ID
 * @returns {Object} Result object
 */
export const removeActivityFromDay = async (tripId, userId, dayId, activityId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const days = tripData.days || [];
    const dayIndex = days.findIndex(d => d.id === dayId);

    if (dayIndex === -1) {
      return { success: false, error: 'Day not found' };
    }

    days[dayIndex].activities = (days[dayIndex].activities || []).filter(a => a.id !== activityId);

    await updateDoc(tripRef, {
      days,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing activity from day:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add an activity to a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {Object} activityData - Activity data (type, vendor, cost, description, memberIds)
 * @returns {Object} Result object
 */
export const addActivityToStay = async (tripId, userId, stayId, activityData) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    const newActivity = {
      id: Date.now().toString(),
      type: activityData.type || '',
      vendor: activityData.vendor || '',
      cost: activityData.cost || '',
      description: activityData.description || '',
      memberIds: activityData.memberIds || [], // Empty array means all members
      createdAt: new Date().toISOString()
    };

    stays[stayIndex].activities = stays[stayIndex].activities || [];
    stays[stayIndex].activities.push(newActivity);

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true, activity: newActivity };
  } catch (error) {
    console.error('Error adding activity to stay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an activity in a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {string} activityId - The activity ID
 * @param {Object} updates - Updates to apply (type, vendor, cost, description)
 * @returns {Object} Result object
 */
export const updateActivityInStay = async (tripId, userId, stayId, activityId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    const activities = stays[stayIndex].activities || [];
    const activityIndex = activities.findIndex(a => a.id === activityId);

    if (activityIndex === -1) {
      return { success: false, error: 'Activity not found' };
    }

    activities[activityIndex] = {
      ...activities[activityIndex],
      ...updates
    };

    stays[stayIndex].activities = activities;

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating activity in stay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove an activity from a stay
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID
 * @param {string} stayId - The stay ID
 * @param {string} activityId - The activity ID
 * @returns {Object} Result object
 */
export const removeActivityFromStay = async (tripId, userId, stayId, activityId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const stays = tripData.stays || [];
    const stayIndex = stays.findIndex(s => s.id === stayId);

    if (stayIndex === -1) {
      return { success: false, error: 'Stay not found' };
    }

    stays[stayIndex].activities = (stays[stayIndex].activities || []).filter(a => a.id !== activityId);

    await updateDoc(tripRef, {
      stays,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing activity from stay:', error);
    return { success: false, error: error.message };
  }
};

// ============================================================================
// MEMBER MANAGEMENT (Partial Trip Participation)
// ============================================================================

/**
 * Add a member to a trip
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID (trip owner)
 * @param {Object} memberData - Member data {id, name, isOwner}
 * @returns {Object} Result object with the added member
 */
export const addMemberToTrip = async (tripId, userId, memberData) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const members = tripData.members || [];

    // Check if member already exists
    if (members.some(m => m.id === memberData.id)) {
      return { success: false, error: 'Member already added to this trip' };
    }

    const newMember = {
      id: memberData.id,
      name: memberData.name,
      isOwner: memberData.isOwner || false
    };

    members.push(newMember);

    await updateDoc(tripRef, {
      members,
      updatedAt: serverTimestamp()
    });

    return { success: true, member: newMember };
  } catch (error) {
    console.error('Error adding member to trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a member from a trip
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID (trip owner)
 * @param {string} memberId - The member ID to remove
 * @returns {Object} Result object
 */
export const removeMemberFromTrip = async (tripId, userId, memberId) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const members = tripData.members || [];
    const filteredMembers = members.filter(m => m.id !== memberId);

    // Allow removing all members - when no members exist, everything shows (default behavior)
    await updateDoc(tripRef, {
      members: filteredMembers,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing member from trip:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a member in a trip
 * @param {string} tripId - The trip ID
 * @param {string} userId - The user ID (trip owner)
 * @param {string} memberId - The member ID to update
 * @param {Object} updates - Updates to apply (name)
 * @returns {Object} Result object
 */
export const updateMemberInTrip = async (tripId, userId, memberId, updates) => {
  try {
    const tripRef = doc(db, TRIPS_COLLECTION, tripId);
    const tripDoc = await getDoc(tripRef);

    if (!tripDoc.exists()) {
      return { success: false, error: 'Trip not found' };
    }

    const tripData = tripDoc.data();

    // Check ownership
    if (tripData.userId !== userId) {
      return { success: false, error: 'Not authorized to modify this trip' };
    }

    const members = tripData.members || [];
    const memberIndex = members.findIndex(m => m.id === memberId);

    if (memberIndex === -1) {
      return { success: false, error: 'Member not found' };
    }

    members[memberIndex] = {
      ...members[memberIndex],
      ...updates
    };

    await updateDoc(tripRef, {
      members,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating member in trip:', error);
    return { success: false, error: error.message };
  }
};
